// Search-intent classifier for generated content pages (glossary today,
// guides/prompts if they grow the same way).
//
// Why this exists: the 2026-08-22 funnel cut showed glossary pages pulling
// 41% of all site entries and converting to a download at 1.8%, against the
// homepage's 14.3%. Splitting that traffic by the intent baked into the slug
// explained the gap almost entirely:
//
//   definitional ("iso-8601-date-file-naming")            0.8%  (364 entries)
//   task         ("organize-files-automatically-windows") 3.4%  ( 87 entries)
//   commercial   ("easy-file-organizer-vs-file-juggler")  7.4%  ( 54 entries)
//
// Window: 2026-08-19..22, non-bot, event_type='page' only (page_alias rows
// are a second copy of a pageview, not traffic). Small absolute numbers, so
// treat the gradient as directional and the exact rates as noisy.
//
// Two thirds of the traffic is encyclopedia lookups from people who are not
// shopping for software, and every one of those pages was rendering the same
// definition template with its only CTA ~83% of the way down. The classifier
// lets the template match the ask: a light nudge on a definition, a real
// product answer on a page someone landed on while comparing tools.
//
// The patterns intentionally mirror the SQL used for that analysis so the
// before/after measurement compares like with like.

// The first three alternatives are exactly the patterns used in the SQL that
// produced the rates above. The four after them are deliberate widenings:
// slugs like "file-organizer-app-windows" or "ai-file-organizer" are plainly
// tool-shopping queries but carry neither "vs" nor a "best-" prefix, so the
// original pass filed them as definitional and served them a definition.
// That widening moves ~12 of the 421 pages from definitional to commercial,
// which shifts both buckets' rates on its own; the follow-up read has to
// account for it rather than treating the move as an effect of the CTA.
const COMMERCIAL = new RegExp(
  [
    'vs-|-vs',
    'alternative',
    'best-[a-z0-9-]*(organizer|app|tool|software)',
    '(organizer|organiser)-(app|software|tool)',
    '-(app|software)-(mac|windows)',
    // "<x>-organization-software", "affordable-ai-file-organization-tool":
    // category-shopping queries that name no competitor and carry no "best".
    '(organization|organisation|organizer|organiser)-(tool|software|app)',
    // Bare product-category head terms: "ai-file-organizer",
    // "free-file-organizer-mac". Note "organization" does not contain
    // "organizer", so this does not swallow the method pages.
    '^(ai|free|top)-[a-z0-9-]*(organizer|organiser|sorter)',
    // A slug carrying a competitor's product name is a switching query even
    // when it is neither a comparison nor an "alternative" page:
    // "hazel-rules-examples" is somebody running Hazel today. Names come from
    // brand/prohibited_terms.json allowed_with_care.competitor_names, minus
    // "Organize My Files", which is also a plain English phrase and matched
    // "can-ai-organize-my-files". Anchored on slug segment boundaries so
    // "mover" cannot match "remover".
    '(^|-)(hazel|dropzone|file-juggler|dropit|belvedere|devonthink|tidy-up'
      + '|folder-tidy|namequick|digital-janitor|filebot|spacedrive'
      + '|total-commander|directory-opus|xyplorer|commander-one|cleanmymac'
      + '|paperless-ngx|floxtop)(-|$)',
  ].join('|')
);
const TASK = /(how-to|automatically|organize-|clean-up|rename-)/;

/**
 * @param {string} slug  page slug, e.g. "organize-screenshots-mac"
 * @param {string} [term] human title, used only as a fallback signal
 * @returns {'commercial'|'task'|'definitional'}
 */
export function classifyIntent(slug, term) {
  const haystack = `${slug || ''} ${(term || '').toLowerCase().replace(/\s+/g, '-')}`.toLowerCase();
  if (COMMERCIAL.test(haystack)) return 'commercial';
  if (TASK.test(haystack)) return 'task';
  return 'definitional';
}

export default classifyIntent;
